import { removeEmptyValues } from "@/lib/utils";
import { sanitizeGatewayPayload } from "../../lib/sanitize-gateway-payload";
import type { McpServer, McpServerCreateRequest } from "../../types";

export const GATEWAY_DEFAULTS: McpServerCreateRequest = {
  name: "",
  description: "",
  instructions: "",
  slug: "",
  status: "active",
  auth_type: "arcade",
  user_source_id: null,
  tool_filter: { allowed_tools: [], discovery: { enabled: false } },
  allowed_passthrough_headers: [],
  consent_skip_client_ids: [],
};

// The only fields a user can realistically submit wrong, in the order we want
// to surface and focus them.
export const INVALID_FIELD_LABELS: Partial<
  Record<keyof McpServerCreateRequest, string>
> = {
  name: "Name",
  user_source_id: "User Source",
  slug: "URL slug",
};

export const gatewayToFormValues = (
  gateway: McpServer
): McpServerCreateRequest => ({
  name: gateway.name,
  description: gateway.description || "",
  instructions: gateway.instructions || "",
  slug: gateway.slug || "",
  status: gateway.status,
  auth_type: gateway.auth_type,
  user_source_id: gateway.user_source_id ?? null,
  tool_filter: {
    allowed_tools: gateway.tool_filter?.allowed_tools ?? [],
    // Round-trip the server's discovery state into the toggle. The engine always
    // returns discovery.enabled, and PUT replaces the config, so the saved value
    // reflects the switch exactly.
    discovery: { enabled: gateway.tool_filter?.discovery?.enabled ?? false },
  },
  allowed_passthrough_headers: gateway.allowed_passthrough_headers ?? [],
  consent_skip_client_ids: gateway.consent_skip_client_ids ?? [],
});

export const getSelectedAppNames = (allowedTools: string[] | undefined) =>
  Array.from(
    new Set(
      (allowedTools ?? [])
        .map((tool) => tool.split(".")[0])
        .filter((name): name is string => Boolean(name))
    )
  );

/** Shared create/update submit transform. */
export type ToGatewayPayloadOptions = {
  /** When false on create, discovery is forced off before submit. */
  toolSearchEnabled?: boolean;
  isEditing?: boolean;
};

export const toGatewayPayload = (
  values: McpServerCreateRequest,
  options: ToGatewayPayloadOptions = {}
) => {
  const { isEditing = false, toolSearchEnabled = true } = options;
  let payload = removeEmptyValues(values);

  if (!(isEditing || toolSearchEnabled)) {
    payload = {
      ...payload,
      tool_filter: {
        allowed_tools: payload.tool_filter?.allowed_tools ?? [],
        discovery: { enabled: false },
      },
    };
  }

  return sanitizeGatewayPayload(payload);
};
